<script setup lang="ts">
import {computed, onMounted, provide, reactive, watch, watchEffect} from 'vue'
import type {DataRow} from '@hantera/design-system'
import {InspectorLayout, type InspectorProvider} from '@hantera/design-system'
import {useContext, useDialogs, useLocalization, useProcesses} from '@hantera/portal-app'
import useBackend from '@/composition/useBackend'
import {useRoleStore} from '@/stores/iam/role-store'
import RoleSidebar from './RoleSidebar.vue'
import RolePermissionsTab from './RolePermissionsTab.vue'
import RoleMembersTab from './RoleMembersTab.vue'

const props = defineProps<{
  row: DataRow
}>()

const roleId = computed(() => props.row.key)

// Initialize services in component context
const context = useContext()
const dialogs = useDialogs()
const processes = useProcesses()
const iamService = useBackend().iam
const t = useLocalization('iam.roles')

// Create Pinia store with services
const store = useRoleStore(roleId.value, {
  context,
  dialogs,
  processes,
  iamService,
  t,
})

// Provide store for tabs
provide('roleStore', store)

// Reactive InspectorProvider (NOT computed!)
const inspectorProvider: InspectorProvider = reactive({
  state: { label: 'Loading' },
  title: '',
  labels: [],
  summary: RoleSidebar,
  summaryLabel: t('overview'),
  selectedTab: 'permissions',
  tabs: [],
})

// Update state/title/labels dynamically
watchEffect(() => {
  if (!store.role) return

  inspectorProvider.state = store.isSystemRole ? { label: t('systemRole'), hue: 0 } : { label: t('customRole'), hue: 200 }

  inspectorProvider.title = store.role.key

  inspectorProvider.labels = [
    t('membersCount', { count: store.role.memberCount }),
  ]

  if (store.isSystemRole) {
    inspectorProvider.labels.splice(0, 0, {
      label: t('systemRole'),
    })
  }
})

// Update tabs dynamically with counts
watchEffect(() => {
  if (!store.role) {
    inspectorProvider.tabs = []
    return
  }

  inspectorProvider.tabs = [
    {
      id: 'permissions',
      label: t('permissions'),
      tags:
        store.role.acl.length > 0
          ? [
            {
              style: 'primary',
              value: store.role.acl.length.toString(),
            },
          ]
          : [],
      component: RolePermissionsTab,
    },
    {
      id: 'members',
      label: t('members'),
      tags:
        store.role.memberCount.gt(0)
          ? [
            {
              style: 'primary',
              value: store.role.memberCount.toString(),
            },
          ]
          : [],
      component: RoleMembersTab,
    },
  ]
})

// Load on mount and when ID changes
onMounted(() => {
  store.refresh()
})

watch(roleId, (newId) => {
  if (newId) {
    store.refresh()
  }
})
</script>

<template>
  <InspectorLayout :provider="inspectorProvider" />
</template>
